import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import workCaseApi from '../services/workCaseApi';
import CreateWorkCaseModal from './workcase/CreateWorkCaseModal';
import WorkCaseList from './workcase/WorkCaseList';
import WorkCaseDetail from './workcase/WorkCaseDetail';
import WorkCaseStatistics from './workcase/WorkCaseStatistics';

const WorkCaseManagement = () => {
  const { user } = useAuth();
  
  // Debug log for user
  useEffect(() => {
    console.log('🔍 [DEBUG] WorkCaseManagement - current user:', user);
    console.log('🔍 [DEBUG] WorkCaseManagement - token exists:', !!sessionStorage.getItem('token'));
    console.log('🔍 [DEBUG] WorkCaseManagement - user from sessionStorage:', sessionStorage.getItem('user'));
  }, [user]);
  const [workCases, setWorkCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    page: 1,
    limit: 10,
    viewMode: 'assigned'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Load work cases
  const loadWorkCases = async () => {
    try {
      setLoading(true);
      const response = await workCaseApi.getMyWorkCases({
        status: filters.status,
        priority: filters.priority,
        category: filters.category,
        page: filters.page,
        limit: filters.limit,
        view: filters.viewMode === 'created' ? 'created' : 'assigned'
      });
      setWorkCases(response.cases);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading work cases:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load work cases when filters change
  useEffect(() => {
    loadWorkCases();
  }, [filters]);

  // Debug log for selectedCase
  useEffect(() => {
    console.log('selectedCase changed:', selectedCase);
  }, [selectedCase]);

  // Create new work case
  const handleCreateWorkCase = async (workCaseData) => {
    try {
      console.log('Creating work case with data:', workCaseData);
      await workCaseApi.createWorkCase(workCaseData);
      setShowCreateModal(false);
      loadWorkCases(); // Reload list
    } catch (error) {
      console.error('Error creating work case:', error);
      throw error;
    }
  };

  // Update work case status
  const handleUpdateStatus = async (caseId, assigneeId, newStatus, notes = '') => {
    try {
      console.log('🔍 [DEBUG] handleUpdateStatus called with:', { caseId, assigneeId, newStatus, notes });
      console.log('🔍 [DEBUG] handleUpdateStatus - current user:', user);
      console.log('🔍 [DEBUG] handleUpdateStatus - token exists:', !!sessionStorage.getItem('token'));
      
      await workCaseApi.updateAssignmentStatus(caseId, assigneeId, { status: newStatus, notes });
      loadWorkCases(); // Reload list
      if (selectedCase && (selectedCase.workCase?.id === caseId || selectedCase.id === caseId)) {
        // Update selected case
        const response = await workCaseApi.getWorkCaseDetail(caseId);
        setSelectedCase(response.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  };

  // Add note to work case
  const handleAddNote = async (caseId, note) => {
    if (!caseId) {
      console.error('Case ID is undefined');
      return;
    }

    console.log('handleAddNote called with caseId:', caseId, 'note:', note);

    try {
      await workCaseApi.addNote(caseId, note);
      if (selectedCase && (selectedCase.workCase?.id === caseId || selectedCase.id === caseId)) {
        // Update selected case
        const response = await workCaseApi.getWorkCaseDetail(caseId);
        setSelectedCase(response.data);
      }
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  };

  // Approve or reject work case
  const handleApproveReject = async (caseId, action, notes = '') => {
    try {
      await workCaseApi.approveRejectWorkCase(caseId, { action, notes });
      loadWorkCases(); // Reload list
      if (selectedCase && (selectedCase.workCase?.id === caseId || selectedCase.id === caseId)) {
        // Update selected case
        const response = await workCaseApi.getWorkCaseDetail(caseId);
        setSelectedCase(response.data);
      }
    } catch (error) {
      console.error('Error approving/rejecting work case:', error);
      throw error;
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    console.log('Filter changed:', newFilters);
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    console.log('Page changed to:', newPage);
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Quản lý Work Cases</h1>
              <p className="mt-2 text-gray-600">
                Tạo và quản lý các công việc được giao
              </p>
            </div>
            <button
              onClick={() => {
                console.log('Opening create modal');
                setShowCreateModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-cols"
            >
              + Tạo Work Case
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setFilters(prev => ({ ...prev, viewMode: 'created', page: 1 }))}
              className={`px-4 py-2 text-sm font-medium ${filters.viewMode === 'created' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Tôi đã giao
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, viewMode: 'assigned', page: 1 }))}
              className={`px-4 py-2 text-sm font-medium border-l border-gray-200 ${filters.viewMode !== 'created' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Được giao cho tôi
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Statistics */}
          <div className="lg:col-span-1">
            <WorkCaseStatistics />
          </div>

          {/* Right Column - Work Cases List & Detail */}
          <div className="lg:col-span-2">
            {selectedCase ? (
              <WorkCaseDetail
                workCase={selectedCase}
                onClose={() => setSelectedCase(null)}
                onUpdateStatus={handleUpdateStatus}
                onAddNote={handleAddNote}
                onApproveReject={handleApproveReject}
                currentUser={user || null}
              />
            ) : (
              <WorkCaseList
                workCases={workCases}
                loading={loading}
                filters={{
                  status: filters.status,
                  priority: filters.priority,
                  category: filters.category,
                  page: filters.page,
                  limit: filters.limit
                }}
                pagination={pagination}
                onFilterChange={handleFilterChange}
                onPageChange={handlePageChange}
                onViewDetail={async (workCaseId) => {
                  console.log('onViewDetail called with id:', workCaseId);
                  try {
                    const response = await workCaseApi.getWorkCaseDetail(workCaseId);
                    if (response.success) {
                      console.log('Full work case detail loaded:', response.data);
                      setSelectedCase(response.data);
                    } else {
                      console.error('Failed to load work case detail:', response.message);
                      setSelectedCase(null);
                    }
                  } catch (error) {
                    console.error('Error loading work case detail:', error);
                    setSelectedCase(null);
                  }
                }}
                onUpdateStatus={handleUpdateStatus}
                onApproveReject={handleApproveReject}
                currentUser={user || null}
              />
            )}
          </div>
        </div>

        {/* Create Work Case Modal */}
        {showCreateModal && (
          <CreateWorkCaseModal
            onClose={() => {
              console.log('Closing create modal');
              setShowCreateModal(false);
            }}
            onSubmit={handleCreateWorkCase}
            currentUser={user || null}
          />
        )}
      </div>
    </div>
  );
};

export default WorkCaseManagement;
